import { isArray } from "lodash";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  AnalyzeCampaignRequestBodyType,
  AnalyzeExperimentRequestBodyType,
  AnalyzeResourceRequestBodyType,
  AnalyzeResourceResponseBodyType,
  AnalyzeSitewideRequestBodyType,
  DatasetId,
  ExperienceDto,
  GetExperienceResponseBodyType,
  MetricArrayType,
} from "@intelligems/ig-types";
import {
  QueryObserverSuccessResult,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  DashboardGraphConfigs,
  GraphConfig,
  GraphConfigGroup,
} from "../../domains/Dashboard/Graphs/Utils/graphConfigs";
import {
  setCompareGroups,
  setDashboardFilterByExperience,
  updateDashboardFilter,
} from "../../store/slices/dashboardFilters";
import { IAppState } from "../../store/types/store";
import { AnalyticsSubMenu } from "../../types/navigation";
import { QueryMeta } from "../../types/react-query";
import { ExperienceFe } from "../../types/schemas/experiences";
import { api } from "../api";
import { useConfiguration } from "../configurations/use-configuration";
import { useGetExperience } from "../experiences/get-experience";
import { useGetExperiencesList } from "../experiences-list/get-experiences-list";
import { experienceFinder, getSubPage } from "../formatters/dashboard";
import { useDashboardPersonalizationOrDefault } from "../personalizations/use-dashboard-personalization-or-default";
import { experienceKeys } from "../queryKeys";

const dashboardKeys = {
  all: [{ topic: "dashboards" }] as const,
  selectedCampaign: (data: AnalyzeCampaignRequestBodyType) =>
    [
      {
        selected: true,
        type: "campaigns",
        ...dashboardKeys.all[0],
        ...data,
      },
    ] as const,
  selectedExperiment: (data: AnalyzeExperimentRequestBodyType) =>
    [
      {
        selected: true,
        type: "experiences",
        ...dashboardKeys.all[0],
        ...data,
      },
    ] as const,
  selectedSitewide: (data: AnalyzeSitewideRequestBodyType) =>
    [
      {
        selected: true,
        type: "sitewide",
        ...dashboardKeys.all[0],
        ...data,
      },
    ] as const,
};

const getAnalysis = async (data: AnalyzeResourceRequestBodyType) => {
  const resp = await api.post<
    AnalyzeResourceResponseBodyType,
    AnalyzeResourceRequestBodyType
  >(`/v2/analytics/analyze-resource`, data);
  return resp.data;
};

const fetchSitewideDashboardData = async (
  data: AnalyzeSitewideRequestBodyType
) => {
  const resp = await api.post<
    AnalyzeResourceResponseBodyType,
    AnalyzeSitewideRequestBodyType
  >(`/v2/analytics/analyze-resource`, data);
  return resp.data;
};

export const useSingleExperienceData = (
  experience: ExperienceFe,
  datasetId: string
): UseQueryResult<AnalyzeResourceResponseBodyType> => {
  const maxZScore = useConfiguration<number>("max_z_score");

  const data: AnalyzeExperimentRequestBodyType = {
    resourceType: "experiences",
    experienceId: experience.id!,
    datasetId: datasetId as DatasetId,
    startTime: null,
    endTime: null,
    testOrdersOnly:
      experience?.experienceAnalysis?.analyticsViewType ===
        "test_orders_only" ||
      experience?.experienceAnalysis?.analyticsViewType === "none",
    discountIncludeType: 1,
    maxZScore: maxZScore!.value,
    customEvents: [],
    countryCodes: [],
    urlParam: {
      queryParam: null,
      queryParamValue: null,
      queryParamFilterType: null,
    },
    deviceType: null,
    visitorType: null,
    shippingExpensePerOrder: null,
    transactionFeePct: null,
    excludeIpAddresses: [],
    channels: [],
    userBehavior: {
      viewedCheckout: null,
      addedToCart: null,
    },
  };

  return useQuery({
    queryKey: [dashboardKeys.selectedExperiment(data)],
    queryFn: async () => getAnalysis(data),
  });
};

const getDatasetIdsOnPage = (graphConfigs: DashboardGraphConfigs) => {
  const datasetIds: DatasetId[] = [];

  graphConfigs.forEach((config: GraphConfig | GraphConfigGroup) => {
    if (!config.isGroup) {
      const data = (config as GraphConfig).datasetId;
      if (isArray(data)) {
        data.forEach((d) => datasetIds.push(d));
      } else {
        datasetIds.push((config as GraphConfig).datasetId as DatasetId);
      }
    } else {
      (config as GraphConfigGroup).configs.forEach((config: GraphConfig) => {
        const data = (config as GraphConfig).datasetId;
        if (isArray(data)) {
          data.forEach((d) => datasetIds.push(d));
        } else {
          datasetIds.push((config as GraphConfig).datasetId as DatasetId);
        }
      });
    }
  });
  return datasetIds;
};

export const useCampaignAnalysis = (
  graphConfigs: DashboardGraphConfigs
): UseQueryResult<AnalyzeResourceResponseBodyType>[] => {
  const resource = useDashboardPersonalizationOrDefault();

  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );

  const datasetIdsOnPage = getDatasetIdsOnPage(graphConfigs);
  return useQueries({
    queries: datasetIdsOnPage.map((datasetId) => {
      let data: AnalyzeCampaignRequestBodyType | null = null;

      if (resource.isSuccess && resource.data !== undefined) {
        const experienceId = (
          resource as QueryObserverSuccessResult<
            GetExperienceResponseBodyType,
            Error
          >
        ).data.experience.id!;

        data = {
          ...dashboardFilters,
          resourceType: "campaigns",
          experienceId,
          datasetId,
          startTime: dashboardFilters.startTime ?? null,
          endTime: dashboardFilters.endTime ?? null,
          shippingExpensePerOrder: null,
          transactionFeePct: null,
          maxZScore: !dashboardFilters.isZScoreEnabled
            ? 9999
            : dashboardFilters.maxZScore,
        };
      }

      return {
        queryKey: [dashboardKeys.selectedCampaign(data!)],
        queryFn: async () => getAnalysis(data!),
        enabled: resource.isSuccess && !!data,
      };
    }),
  });
};

export const useSitewideAnalysis = (
  graphConfigs: DashboardGraphConfigs
): UseQueryResult<AnalyzeResourceResponseBodyType>[] => {
  const sitewideDashboardFilters = useSelector(
    (state: IAppState) => state.sitewideDashboardFilters
  );

  const datasetIdsOnPage: DatasetId[] = getDatasetIdsOnPage(graphConfigs);

  return useQueries({
    queries: datasetIdsOnPage.map((datasetId) => {
      let data: AnalyzeSitewideRequestBodyType | null = null;
      data = {
        ...sitewideDashboardFilters,
        datasetId: datasetId,
        resourceType: "sitewide",
        startTime: sitewideDashboardFilters.startTime ?? null,
        endTime: sitewideDashboardFilters.endTime ?? null,
      };

      return {
        queryKey: [dashboardKeys.selectedSitewide(data!)],
        enabled: !!data,
        meta: {
          onError: {
            "503": "ignore",
          },
        } as QueryMeta,
        queryFn: async () => fetchSitewideDashboardData(data!),
      };
    }),
  });
};

export const useExperimentAnalysis = (
  graphConfigs: DashboardGraphConfigs
): UseQueryResult<AnalyzeResourceResponseBodyType>[] => {
  const resource = useDashboardExperienceOrDefault();

  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );

  const datasetIdsOnPage: DatasetId[] = getDatasetIdsOnPage(graphConfigs);
  return useQueries({
    queries: datasetIdsOnPage.map((datasetId: DatasetId) => {
      let data: AnalyzeExperimentRequestBodyType | null = null;
      const experienceResource = resource as QueryObserverSuccessResult<
        GetExperienceResponseBodyType,
        Error
      >;
      if (resource.isSuccess && resource.data !== undefined) {
        data = {
          ...dashboardFilters,
          datasetId,
          resourceType: "experiences",
          experienceId: experienceResource.data.experience.id!,
          startTime: dashboardFilters.startTime ?? null,
          endTime: dashboardFilters.endTime ?? null,
          testOrdersOnly:
            dashboardFilters.testOrdersOnly !== undefined
              ? dashboardFilters.testOrdersOnly
              : experienceResource.data?.experience?.experienceAnalysis
                  ?.analyticsViewType === "test_orders_only" ||
                experienceResource.data?.experience?.experienceAnalysis
                  ?.analyticsViewType === "none",
          maxZScore: !dashboardFilters.isZScoreEnabled
            ? "9999"
            : dashboardFilters.maxZScore || null,
        };
      }

      return {
        queryKey: [dashboardKeys.selectedExperiment(data!)],
        meta: {
          onError: {
            "503": "ignore",
          },
        } as QueryMeta,
        queryFn: async () => getAnalysis(data!),
        enabled: resource.isSuccess && !!data,
      };
    }),
  });
};

export const useDashboardExperienceOrDefault = (
  dashboardData?: MetricArrayType
) => {
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );
  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );
  const maxZScore = useConfiguration<number>("max_z_score");

  const queryClient = useQueryClient();
  const location: any = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const urlId =
    new URLSearchParams(location.search).get("experienceId") || undefined;

  useEffect(() => {
    let correctExperience: ExperienceDto | undefined = undefined;
    if (experiencesList.isSuccess) {
      // If the experience ID is a url param, always use it
      if (urlId) {
        const cachedExperience: ExperienceDto | undefined =
          experiencesList.data.experiencesList.find((exp) => exp.id === urlId);

        if (cachedExperience) {
          correctExperience = cachedExperience;
        }
      } else {
        //  Backup 1 -> use the dashboard filter ID
        const cachedExperience: GetExperienceResponseBodyType | undefined =
          dashboardFilters.experienceId
            ? queryClient.getQueryData(
                experienceKeys.one(dashboardFilters.experienceId)
              )
            : undefined;

        if (cachedExperience) {
          correctExperience = cachedExperience.experience;
        } else {
          //  Backup 2 - use default experience
          const defaultExperience = experienceFinder(
            experiencesList.data.experiencesList
          );
          if (defaultExperience) {
            correctExperience = defaultExperience;
          }
        }
      }

      if (correctExperience) {
        if (correctExperience.id !== dashboardFilters.experienceId) {
          dispatch(
            setDashboardFilterByExperience({
              experience: correctExperience,
              page:
                getSubPage(location.pathname, AnalyticsSubMenu) ||
                AnalyticsSubMenu.Overview,
              dashboardData,
              shouldShowProfit: shouldShowProfit!.value!,
              maxZScore: maxZScore!.value,
            })
          );
        } else if (
          !dashboardFilters.compareGroupA ||
          !dashboardFilters.compareGroupB
        ) {
          if (!dashboardFilters.maxZScore) {
            dispatch(
              updateDashboardFilter({
                key: "maxZScore",
                value: maxZScore!.value,
              })
            );
          }
          dispatch(
            setCompareGroups({
              experience: correctExperience,
              page:
                getSubPage(location.pathname, AnalyticsSubMenu) ||
                AnalyticsSubMenu.Overview,
              dashboardData,
              shouldShowProfit: shouldShowProfit!.value,
            })
          );
        }

        // make sure filters are correctly loaded in
        const url = new URL(window.location.href);
        url.searchParams.set("experienceId", correctExperience.id!);
        navigate(`${url.pathname}${url.search}`);
      } else {
        // Either there's no completed/active vars, or (maybe?) something went wrong;
        // Show Empty Dialog
        dispatch(updateDashboardFilter({ key: "experienceId", value: null }));
      }
    }
  }, [
    experiencesList.isSuccess,
    experiencesList.data?.experiencesList,
    dashboardData,
    urlId,
  ]);

  // if the requested exp id is not within the org, we use the urlId so the hook can switch by exp id
  // otherwise, let the hook use the dashboard filter id
  const withinOrg = experiencesList.data?.experiencesList.find(
    (experience) => experience.id === urlId
  );
  const queryId = withinOrg ? dashboardFilters.experienceId : urlId;
  const query = useGetExperience(queryId);

  useEffect(() => {
    // check if experience already set so we persist filters across dashboard pages
    if (query.isSuccess && !dashboardFilters.experienceId) {
      dispatch(
        setDashboardFilterByExperience({
          experience: query.data.experience,
          page:
            getSubPage(location.pathname, AnalyticsSubMenu) ||
            AnalyticsSubMenu.Overview,
          dashboardData,
          shouldShowProfit: shouldShowProfit!.value!,
          maxZScore: maxZScore!.value,
        })
      );
    }
  }, [query.isSuccess, dashboardFilters.experienceId]);

  return query;
};
